/**
 * Keeps the sticky add-to-cart size selector in sync with the main variant picker.
 *
 * The main variant picker stays the single source of truth: changing the sticky
 * selector checks the matching input in the picker and lets the theme's own
 * variant-change pipeline update price, availability and the add-to-cart button.
 */

/**
 * @param {string} productId
 * @returns {Element | null}
 */
function getVariantPicker(productId) {
  return document.querySelector(`variant-picker[data-product-id="${productId}"]`);
}

/**
 * Finds the option input matching a value within the picker.
 * @param {Element} picker
 * @param {string} value
 * @returns {HTMLInputElement | null}
 */
function findInput(picker, value) {
  const inputs = /** @type {NodeListOf<HTMLInputElement>} */ (picker.querySelectorAll('input[type="radio"]'));
  for (const input of inputs) {
    if (input.value === value) return input;
  }
  return null;
}

/**
 * Applies the sticky selector's value to the main variant picker.
 * @param {HTMLSelectElement} select
 */
function applyToPicker(select) {
  const productId = select.dataset.productId;
  if (!productId) return;

  const picker = getVariantPicker(productId);
  if (!picker) return;

  const input = findInput(picker, select.value);
  if (!input || input.checked) return;

  // Click rather than setting `checked` so the theme's own listeners fire.
  input.click();
}

/**
 * Mirrors the picker's current selection back onto the sticky selector.
 * @param {HTMLSelectElement} select
 */
function syncFromPicker(select) {
  const productId = select.dataset.productId;
  if (!productId) return;

  const picker = getVariantPicker(productId);
  if (!picker) return;

  const checked = /** @type {HTMLInputElement | null} */ (picker.querySelector('input[type="radio"]:checked'));
  if (checked && checked.value !== select.value) {
    select.value = checked.value;
  }
}

document.addEventListener('change', (event) => {
  const target = /** @type {HTMLElement} */ (event.target);
  if (!(target instanceof HTMLSelectElement)) return;

  if (target.hasAttribute('data-sticky-size-select')) {
    applyToPicker(target);
    return;
  }
});

// Keep the sticky selector current when the shopper changes size in the main picker.
document.addEventListener('change', (event) => {
  const target = /** @type {HTMLElement} */ (event.target);
  if (!(target instanceof HTMLInputElement) || target.type !== 'radio') return;

  for (const select of document.querySelectorAll('[data-sticky-size-select]')) {
    syncFromPicker(/** @type {HTMLSelectElement} */ (select));
  }
});

// Re-sync after the section re-renders on variant change.
document.addEventListener('DOMContentLoaded', () => {
  for (const select of document.querySelectorAll('[data-sticky-size-select]')) {
    syncFromPicker(/** @type {HTMLSelectElement} */ (select));
  }
});
